/**
 * Assert that the provided value will never be reached.
 * Due to this function accepting and returning `never`, typescript will
 * be able to infer that the code should always be unreachable. For cases
 * where that is violated at runtime for whatever reason, an exception
 * would be thrown.
 *
 * This is a very useful function for making sure that all possible `switch` cases
 * are handled. That allows typecheck to give feedback of every part of the code
 * that has to be corrected when a new enum variant is introduced.
 *
 * ```
 * enum MyEnum { A, B }
 *
 * switch (enumValue) {
 *   case MyEnum.A:
 *     return handleA()
 *   case MyEnum.B:
 *     return handleB()
 *   default:
 *     // this is a type error if we ever add MyEnum.C variant.
 *     return assertNever(enumValue)
 * }
 * ```
 */
export function assertNever(_x: never, message?: string): never {
  throw new Error(message || 'Unreachable');
}

/**
 * Assert that the provided value is a number
 */
export function assertNum(value: unknown): asserts value is number {
  if (typeof value !== 'number') {
    throw new Error('expected number');
  }
}

/**
 * Assert that the provided condition will always be met.
 * Allows for specifying a message to throw if the condition is violated.
 *
 * The trueness of the condition is further propagated to the typechecker,
 * so that the following code is free to assume that the condition will always be true.
 */
export function assert(
  condition: boolean,
  message?: string
): asserts condition {
  if (!condition) {
    throw new Error(message ?? 'Assertion failed');
  }
}

/**
 * A shortcut for throwing `Not yet implemented` error.
 * Useful during development.
 */
export function todo(message?: string): never {
  throw new Error('Not yet implemented' + message ? `: ${message}` : '');
}

/**
 * Check if given value is defined (not null or undefined).
 *
 * The result of that check is propagated to the types,
 * which makes it useful for filtering arrays of nullables in order
 * to simplify their types to just the non-nullable part.
 */
export function isDef<T>(v: T | null | undefined): v is T {
  return v != null;
}
